/**
 * Notification feed DTOs. Mirror the .NET `NotificationDto` and replace the legacy Firestore
 * `NOTIFICATION_LOGS` document shape read by the web notification panel.
 *
 * `companyId` is the nullable owning org (null for cross-org partner notifications). `data` is
 * the free-form navigation payload (creditorInvoiceId / inviteToken / partnerEmail / entityType
 * / entityId / projectId) the panel reads for click-through. Timestamps are ISO-8601 strings.
 */
export type NotificationDeliveryStatus = "sent" | "delivered" | "failed";

/**
 * Creditor-invoice (e-faktura) notification types, mirroring
 * `NotificationValues.CreditorInvoiceReceivedType` / `CreditorInvoiceUnmatchedType` on the .NET
 * side. Exported so the web panel and the mobile feed share one spelling instead of each
 * hardcoding the literals — adding a type server-side then only has to be added here.
 */
export const CREDITOR_INVOICE_RECEIVED = "creditor_invoice_received";
export const CREDITOR_INVOICE_UNMATCHED = "creditor_invoice_unmatched";

/** Every creditor-invoice notification type, for tap/click-through targeting. */
export const CREDITOR_INVOICE_NOTIFICATION_TYPES: readonly string[] = [
  CREDITOR_INVOICE_RECEIVED,
  CREDITOR_INVOICE_UNMATCHED,
];

export interface NotificationDto {
  id: string;
  userId: string;
  companyId?: string | null;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  sentAt: string;
  readAt?: string | null;
  clickedAt?: string | null;
  deliveryStatus: NotificationDeliveryStatus;
  errorMessage?: string | null;
}

/**
 * A user's own notification preferences — device/delivery behaviour for one person,
 * distinct from the org-wide notification-settings event rules. At send time the two
 * are ANDed: org rules decide what the company sends, these decide what the user
 * accepts, so a user can narrow but never widen.
 */
export interface NotificationPreferencesDto {
  enabled: boolean;

  taskAssigned: boolean;
  taskReminder: boolean;
  taskUpdated: boolean;
  timerReminder: boolean;
  customerMessage: boolean;
  invoicePaid: boolean;
  approvalRequired: boolean;
  serviceDue: boolean;
  klsReminder: boolean;
  creditorInvoiceReceived: boolean;
  materialAdded: boolean;
  materialApprovalUpdate: boolean;
  quoteResponse: boolean;
  partnerInvitation: boolean;
  defectAssigned: boolean;
  timeplanUpdates: boolean;

  sound: boolean;
  vibration: boolean;
  badge: boolean;

  quietHoursEnabled: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;

  taskReminderMinutes: number;
  serviceDueReminderDays: number;
}
